import React, { useEffect, useState } from 'react';
import { Drawer, Form, Input, InputNumber, Select, Switch, Button, Space, message, Modal } from 'antd';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { formatTimestampToDateTime } from 'utils/dateTimeUtils';
import {
    CREATE_COMMON_CONFIG,
    UPDATE_COMMON_CONFIG,
    GET_COMMON_CONFIG_BY_ID,
    FIND_COMMON_CONFIG_DUPLICATES,
    GET_COMMON_CONFIG_COUNT_BY_KEY,
    CreateCommonConfigInput,
    UpdateCommonConfigInput,
    CreateCommonConfigResponse,
    UpdateCommonConfigResponse,
    GetCommonConfigByIdResponse,
    FindCommonConfigDuplicatesResponse,
    FindCommonConfigDuplicatesVariables,
    GetCommonConfigCountByKeyResponse,
    GetCommonConfigCountByKeyVariables,
    CommonConfigType
} from '../graphql/commonConfigQueries';
import MatchRuleDebugModal from './MatchRuleDebugModal';
import { validateConfigValue } from '../utils/configValueValidator';

const { TextArea } = Input;
const { Option } = Select;

interface CommonConfigDrawerProps {
    visible: boolean;
    onClose: () => void;
    configId?: string | null;
    mode: 'create' | 'edit' | 'view';
    onSuccess?: () => void;
}

const CommonConfigDrawer: React.FC<CommonConfigDrawerProps> = ({
    visible,
    onClose,
    configId,
    mode,
    onSuccess
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const apolloClient = useApolloClient();
    const [debugModalVisible, setDebugModalVisible] = useState(false);
    const [debugInitialExpression, setDebugInitialExpression] = useState<string>('');

    // 查询配置详情（编辑或查看模式）
    const { data: configData, loading: queryLoading } = useQuery<GetCommonConfigByIdResponse>(
        GET_COMMON_CONFIG_BY_ID,
        {
            variables: { id: configId },
            skip: !configId || mode === 'create',
            fetchPolicy: 'network-only'
        }
    );

    // 创建配置
    const [createConfig, { loading: createLoading }] = useMutation<CreateCommonConfigResponse>(
        CREATE_COMMON_CONFIG,
        {
            onCompleted: () => {
                message.success(t('commonConfig.messages.createSuccess'));
                onSuccess?.();
                onClose();
                form.resetFields();
            },
            onError: (error) => {
                message.error(t('commonConfig.messages.createFailed', { error: error.message }));
            }
        }
    );

    // 更新配置
    const [updateConfig, { loading: updateLoading }] = useMutation<UpdateCommonConfigResponse>(
        UPDATE_COMMON_CONFIG,
        {
            onCompleted: () => {
                message.success(t('commonConfig.messages.updateSuccess'));
                onSuccess?.();
                onClose();
            },
            onError: (error) => {
                message.error(t('commonConfig.messages.updateFailed', { error: error.message }));
            }
        }
    );

    // 当配置数据加载完成时，填充表单
    useEffect(() => {
        if (configData?.getCommonConfigById && visible) {
            const config = configData.getCommonConfigById;
            form.setFieldsValue({
                configKey: config.configKey,
                matchRule: config.matchRule,
                sortOrder: config.sortOrder,
                configValue: config.configValue,
                description: config.description,
                configType: config.configType,
                isActive: config.isActive
            });
        }
    }, [configData, visible, form]);

    // 关闭抽屉时重置表单
    useEffect(() => {
        if (!visible) {
            form.resetFields();
        }
    }, [visible, form]);

    // 归一化（仅用于弹窗展示）：sortOrder 空 → -1
    const normalizeSortOrderForDisplay = (v: unknown): number =>
        typeof v === 'number' && Number.isFinite(v) ? v : -1;

    // 调用后端重复校验接口（后端归一化 sortOrder + 精确匹配 + 排除自身；不再比较 matchRule）
    const fetchDuplicateConfigs = async (
        configKey: string,
        sortOrder: number | null | undefined
    ): Promise<CommonConfigType[]> => {
        const variables: FindCommonConfigDuplicatesVariables = {
            configKey,
            sortOrder: sortOrder ?? null,
            excludeId: mode === 'edit' && configId ? configId : null
        };
        const { data } = await apolloClient.query<FindCommonConfigDuplicatesResponse>({
            query: FIND_COMMON_CONFIG_DUPLICATES,
            variables,
            fetchPolicy: 'network-only'
        });
        return data?.findCommonConfigDuplicates || [];
    };

    // 调用后端单 configKey 行数上限校验接口
    const fetchConfigKeyCount = async (
        configKey: string
    ): Promise<{ currentCount: number; limit: number }> => {
        const variables: GetCommonConfigCountByKeyVariables = { configKey };
        const { data } = await apolloClient.query<GetCommonConfigCountByKeyResponse>({
            query: GET_COMMON_CONFIG_COUNT_BY_KEY,
            variables,
            fetchPolicy: 'network-only'
        });
        return data?.getCommonConfigCountByKey || { currentCount: 0, limit: 50 };
    };

    const doSubmit = async (values: any) => {
        if (mode === 'create') {
            const input: CreateCommonConfigInput = {
                configKey: values.configKey,
                configValue: values.configValue,
                description: values.description,
                configType: values.configType || 'STRING',
                isActive: values.isActive !== undefined ? values.isActive : true,
                matchRule: values.matchRule,
                sortOrder: values.sortOrder
            };
            await createConfig({ variables: { input } });
        } else if (mode === 'edit') {
            const input: UpdateCommonConfigInput = {
                configValue: values.configValue,
                description: values.description,
                configType: values.configType,
                isActive: values.isActive,
                matchRule: values.matchRule,
                sortOrder: values.sortOrder
            };
            await updateConfig({
                variables: {
                    id: configId!,
                    input
                }
            });
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const purpleOk = {
                style: {
                    backgroundColor: '#722ed1',
                    borderColor: '#722ed1'
                }
            };

            // 新增模式：先校验单 configKey 行数上限；达到上限直接拒绝，不再走重复校验
            if (mode === 'create') {
                const { currentCount, limit } = await fetchConfigKeyCount(values.configKey);
                if (currentCount >= limit) {
                    Modal.warning({
                        title: t('commonConfig.confirm.limitExceeded.title'),
                        content: t('commonConfig.confirm.limitExceeded.content', {
                            configKey: values.configKey,
                            currentCount,
                            limit
                        }),
                        okText: t('commonConfig.confirm.okText'),
                        okButtonProps: purpleOk
                    });
                    return;
                }
            }

            const duplicates = await fetchDuplicateConfigs(
                values.configKey,
                values.sortOrder
            );

            if (duplicates.length > 0) {
                const displaySortOrder = normalizeSortOrderForDisplay(values.sortOrder);
                // 同顺序下「打开」状态最多一条；本次目标为 active 时若已有 active 行 → 硬阻塞
                const targetIsActive = values.isActive !== false;
                const activeConflicts = targetIsActive ? duplicates.filter(d => d.isActive) : [];
                const blocked = activeConflicts.length > 0;

                const renderRows = (rows: CommonConfigType[]) => (
                    <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
                        {rows.map(d => (
                            <div key={d.id} style={{ padding: 8, borderBottom: '1px solid #f5f5f5', fontSize: 12 }}>
                                <div><strong>ID:</strong> {d.id}</div>
                                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                    <strong>{t('commonConfig.table.columns.matchRule')}:</strong> {d.matchRule || '-'}
                                </div>
                                <div><strong>{t('commonConfig.table.columns.configValue')}:</strong> {d.configValue || '-'}</div>
                                <div><strong>{t('commonConfig.table.columns.description')}:</strong> {d.description || '-'}</div>
                                <div><strong>{t('commonConfig.table.columns.status')}:</strong> {d.isActive ? t('commonConfig.table.status.active') : t('commonConfig.table.status.inactive')}</div>
                                <div><strong>{t('commonConfig.table.columns.updatedAt')}:</strong> {d.updatedAt ? formatTimestampToDateTime(d.updatedAt) : '-'}</div>
                            </div>
                        ))}
                    </div>
                );

                if (blocked) {
                    Modal.warning({
                        title: t('commonConfig.confirm.activeConflict.title'),
                        width: 640,
                        content: (
                            <div>
                                <div style={{ marginBottom: 8 }}>
                                    {t('commonConfig.confirm.activeConflict.content', {
                                        configKey: values.configKey,
                                        sortOrder: displaySortOrder
                                    })}
                                </div>
                                {renderRows(activeConflicts)}
                            </div>
                        ),
                        okText: t('commonConfig.confirm.okText'),
                        okButtonProps: purpleOk
                    });
                    return;
                }

                // 存在相同配置（仅顺序冲突，无 active 冲突）：弹窗展示现有值，确认后允许添加
                Modal.confirm({
                    title: t('commonConfig.confirm.duplicate.title'),
                    width: 640,
                    content: (
                        <div>
                            <div style={{ marginBottom: 8 }}>
                                {t('commonConfig.confirm.duplicate.content', {
                                    configKey: values.configKey,
                                    sortOrder: displaySortOrder
                                })}
                            </div>
                            {renderRows(duplicates)}
                        </div>
                    ),
                    okText: t('commonConfig.confirm.okText'),
                    cancelText: t('commonConfig.confirm.cancelText'),
                    okButtonProps: purpleOk,
                    onOk: async () => {
                        await doSubmit(values);
                    }
                });
                return;
            }

            const confirmKey = mode === 'create' ? 'save' : 'update';
            Modal.confirm({
                title: t(`commonConfig.confirm.${confirmKey}.title`),
                content: t(`commonConfig.confirm.${confirmKey}.content`),
                okText: t('commonConfig.confirm.okText'),
                cancelText: t('commonConfig.confirm.cancelText'),
                okButtonProps: purpleOk,
                onOk: async () => {
                    await doSubmit(values);
                }
            });
        } catch (error) {
            console.error('表单验证失败:', error);
        }
    };

    const isReadOnly = mode === 'view';
    const loading = queryLoading || createLoading || updateLoading;

    // 校验配置值是否合法，仅提示、不影响编辑/保存主流程
    const handleValidate = () => {
        const configType = (form.getFieldValue('configType') as string) || 'STRING';
        const configValue = form.getFieldValue('configValue') as string | undefined;
        if (validateConfigValue(configType, configValue)) {
            message.success(t('commonConfig.messages.validatePass') as string);
        } else {
            message.error(t('commonConfig.messages.validateFail') as string);
        }
    };

    const getTitle = () => {
        switch (mode) {
            case 'create':
                return t('commonConfig.drawer.title.create');
            case 'edit':
                return t('commonConfig.drawer.title.edit');
            case 'view':
                return t('commonConfig.drawer.title.view');
            default:
                return t('commonConfig.drawer.title.view');
        }
    };

    return (
        <Drawer
            title={getTitle()}
            width={600}
            open={visible}
            onClose={onClose}
            maskClosable={false}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    configType: 'STRING',
                    isActive: true,
                    sortOrder: -1
                }}
            >
                <Form.Item
                    name="configKey"
                    label={t('commonConfig.drawer.form.configKey.label') as string}
                    rules={[
                        { required: true, message: t('commonConfig.drawer.form.configKey.required') as string },
                        { max: 255, message: t('commonConfig.drawer.form.configKey.maxLength') as string }
                    ]}
                >
                    <Input
                        placeholder={t('commonConfig.drawer.form.configKey.placeholder') as string}
                        disabled={isReadOnly || mode === 'edit'}
                    />
                </Form.Item>

                <Form.Item
                    name="sortOrder"
                    label={t('commonConfig.drawer.form.sortOrder.label') as string}
                >
                    <InputNumber
                        className="w-full"
                        placeholder={t('commonConfig.drawer.form.sortOrder.placeholder') as string}
                        disabled={isReadOnly}
                        precision={0}
                        step={1}
                        parser={(v) => (v ? v.replace(/[^\d-]/g, '') : '') as unknown as number}
                    />
                </Form.Item>

                <Form.Item
                    name="matchRule"
                    label={
                        <div className="flex items-center justify-between w-full" style={{ width: '100%' }}>
                            <span>{t('commonConfig.drawer.form.matchRule.label')}</span>
                            {!isReadOnly && (
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => {
                                        setDebugInitialExpression(
                                            (form.getFieldValue('matchRule') as string) || ''
                                        );
                                        setDebugModalVisible(true);
                                    }}
                                >
                                    {t('commonConfig.drawer.form.matchRule.debugButton')}
                                </Button>
                            )}
                        </div>
                    }
                >
                    <TextArea
                        rows={2}
                        placeholder={t('commonConfig.drawer.form.matchRule.placeholder') as string}
                        disabled={isReadOnly}
                    />
                </Form.Item>

                <Form.Item
                    name="configValue"
                    label={t('commonConfig.drawer.form.configValue.label') as string}
                >
                    <TextArea
                        rows={4}
                        placeholder={t('commonConfig.drawer.form.configValue.placeholder') as string}
                        disabled={isReadOnly}
                    />
                </Form.Item>

                <Form.Item
                    name="description"
                    label={t('commonConfig.drawer.form.description.label') as string}
                    rules={[{ max: 500, message: t('commonConfig.drawer.form.description.maxLength') as string }]}
                >
                    <TextArea
                        rows={3}
                        placeholder={t('commonConfig.drawer.form.description.placeholder') as string}
                        disabled={isReadOnly}
                    />
                </Form.Item>

                <Form.Item
                    name="configType"
                    label={t('commonConfig.drawer.form.configType.label') as string}
                    rules={[{ required: true, message: t('commonConfig.drawer.form.configType.required') as string }]}
                >
                    <Select placeholder={t('commonConfig.drawer.form.configType.placeholder') as string} disabled={isReadOnly}>
                        <Option value="STRING">{t('commonConfig.configType.STRING')}</Option>
                        <Option value="JSON">{t('commonConfig.configType.JSON')}</Option>
                        <Option value="NUMBER">{t('commonConfig.configType.NUMBER')}</Option>
                        <Option value="BOOLEAN">{t('commonConfig.configType.BOOLEAN')}</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="isActive"
                    label={t('commonConfig.drawer.form.isActive.label') as string}
                    valuePropName="checked"
                >
                    <Switch
                        checkedChildren={t('commonConfig.drawer.form.isActive.enabled') as string}
                        unCheckedChildren={t('commonConfig.drawer.form.isActive.disabled') as string}
                        disabled={isReadOnly}
                    />
                </Form.Item>

                {!isReadOnly && (
                    <Form.Item>
                        <div className="flex justify-end">
                            <Space>
                                <Button onClick={handleValidate}>{t('commonConfig.drawer.buttons.validate')}</Button>
                                <Button onClick={onClose}>{t('commonConfig.drawer.buttons.cancel')}</Button>
                                <Button type="primary" onClick={handleSubmit} loading={loading}>
                                    {mode === 'create' ? t('commonConfig.drawer.buttons.create') : t('commonConfig.drawer.buttons.save')}
                                </Button>
                            </Space>
                        </div>
                    </Form.Item>
                )}
            </Form>
            <MatchRuleDebugModal
                visible={debugModalVisible}
                initialExpression={debugInitialExpression}
                onClose={(finalExpression, confirmed) => {
                    setDebugModalVisible(false);
                    if (confirmed) {
                        form.setFieldValue('matchRule', finalExpression);
                    }
                }}
            />
        </Drawer>
    );
};

export default CommonConfigDrawer;

